/**
 * Metre (*chandas*).
 *
 * Sanskrit verse is composed in fixed metres, and knowing which one a verse is
 * in tells a reader how to chant it and often how to resolve an ambiguous
 * reading. The catalogue of *vṛtta* and *jāti* metres is embedded in the .wasm
 * we already load, so this costs nothing beyond the code.
 *
 * The match is reported with its strength: a `full` match means all four pādas
 * fit, `pada` means one line does, `prefix` means only the opening. A half-verse
 * will usually come back as `pada`, which is still worth showing.
 */

import { iastToSlp1, loadEngine, slp1ToIast } from "./vidyut-engine";

export type MeterMatch = {
    /** The metre's name, e.g. "vasantatilakā". */
    name: string;
    /** How much of the text fits it. */
    matchType: "none" | "prefix" | "pada" | "full";
};

type ChandasEngine = {
    findMeters(text: string): Array<{ name: string; matchType: MeterMatch["matchType"] }>;
    free(): void;
};

let chandas: ChandasEngine | null = null;
let pending: Promise<ChandasEngine | null> | null = null;

/** Loads the metre catalogue, once. Shares the .wasm with the grammar engine. */
export const loadChandas = (): Promise<ChandasEngine | null> => {
    if (chandas) return Promise.resolve(chandas);
    if (pending) return pending;

    pending = (async () => {
        try {
            await loadEngine();
            const vidyut: any = await import("@siva-sh/vidyut");
            chandas = new vidyut.Chandas() as ChandasEngine;
            return chandas;
        } catch (e) {
            console.error("vidyut: failed to load the metre catalogue", e);
            return null;
        } finally {
            // A failed load must not be remembered; see vidyut-engine.ts.
            if (!chandas) pending = null;
        }
    })();

    return pending;
};

/**
 * The metres a passage fits, strongest match first.
 *
 * Returns nothing for prose, for a fragment too short to scan, and for a verse
 * in a metre outside the catalogue — all of which are ordinary, so the caller
 * should treat an empty result as "no comment" rather than as an error.
 */
export const findMeters = async (text: string): Promise<MeterMatch[]> => {
    const engine = await loadChandas();
    if (!engine) return [];

    const clean = text.trim();
    if (clean.length < 8) return [];

    let found;
    try {
        found = engine.findMeters(iastToSlp1(clean));
    } catch (e) {
        console.warn("vidyut: metre detection failed", e);
        return [];
    }

    const rank: Record<MeterMatch["matchType"], number> = {
        full: 0, pada: 1, prefix: 2, none: 3,
    };
    return found
        .filter(m => m.matchType !== "none" && m.name)
        // The catalogue names its metres in SLP1; show them as the rest of the
        // site writes Sanskrit.
        .map(m => ({ ...m, name: slp1ToIast(m.name) }))
        .sort((a, b) => rank[a.matchType] - rank[b.matchType]);
};
